import { getShareOpenUrl } from "@/api/files-hub/fileShares.api";
import { setNotificationRead } from "@/api/notifications/notifications.mutations";
import {
  getNotifications,
  NotificationsQueryKeys,
} from "@/api/notifications/notifications.queries";
import type { NotificationItem } from "@/api/notifications/notifications.types";
import {
  getProcurementExpiryAlerts,
  type ProcurementExpiryAlert,
  ProcurementAlertQueryKeys,
} from "@/api/procurement/procurement.expiryAlerts";
import {
  ActionIcon,
  Box,
  Center,
  Flex,
  Indicator,
  Loader,
  Popover,
  ScrollArea,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications as mantineNotifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconBell,
  IconBriefcase,
  IconCashBanknote,
  IconClockHour4,
  IconDeviceAnalytics,
  IconFolderShare,
  IconMap2,
  IconSettings2,
  IconFileText,
  IconShoppingCart,
  IconUserSearch,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toNavigateTarget } from "@/utils/postLoginReturn";
import { formatDistanceToNow } from "date-fns";

const UNREAD_POLL_MS = 30_000;

const CATEGORY_META: Record<
  string,
  {
    icon: React.ComponentType<{ size?: number }>;
    route?: string;
    /* The procurement request tab a notification of this category opens on. */
    requestTab?: string;
  }
> = {
  work_order: { icon: IconBriefcase, route: "/work-orders" },
  visit: { icon: IconMap2, route: "/work-orders" },
  payment: { icon: IconCashBanknote, route: "/accounts-payable" },
  auto_dispatch: { icon: IconDeviceAnalytics, route: "/dispatch-queue" },
  reactive_hire: { icon: IconUserSearch, route: "/service-providers/reactive-hire-requests" },
  proposal: { icon: IconFileText, route: "/proposals" },
  // route is unused - a file_share notification is opened through its share's open-url.
  files: { icon: IconFolderShare },
  // SERV-3020: route is unused for this category - a procurement notification always carries a
  // resourceId and is handled by the resourceType branch in handleItemClick below, the same way
  // payment_request is, because a static route can only land on the list, never the one request.
  procurement: { icon: IconShoppingCart },
  // SERV-3353: a reply, note mention or dispatcher message opens the request on its thread.
  procurement_conversation: { icon: IconShoppingCart, requestTab: "conversation" },
  alert: { icon: IconAlertTriangle },
  reminder: { icon: IconClockHour4 },
  system: { icon: IconSettings2 },
};

/* Worst reads red, not merely different: an expired rental is billing money right now. */
const ALERT_TONE: Record<ProcurementExpiryAlert["severity"], string> = {
  expired: "var(--text-danger-default)",
  urgent: "var(--text-warning-default)",
  soon: "var(--text-weak)",
};

const ALERT_HEADING: Record<ProcurementExpiryAlert["severity"], string> = {
  expired: "Overdue",
  urgent: "Ends soon",
  soon: "Upcoming",
};

export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, { toggle, close }] = useDisclosure(false);

  const { data: unreadData } = useQuery({
    queryKey: [NotificationsQueryKeys.unreadCount],
    queryFn: ({ signal }) => getNotifications({ isRead: false, limit: 1 }, signal),
    refetchInterval: UNREAD_POLL_MS,
  });
  const storedUnread = unreadData?.pagination.total ?? 0;

  const { data: listData, isLoading } = useQuery({
    queryKey: [NotificationsQueryKeys.list],
    queryFn: ({ signal }) => getNotifications({ limit: 20 }, signal),
    enabled: isOpen,
  });
  const notifications = listData?.data ?? [];

  /*
   * Procurement expiry alerts are computed on read rather than stored, so they arrive from their
   * own endpoint and sit above the stored notifications. They carry no read state - there is no row
   * to mark - which is why they are kept separate rather than folded into `notifications`: marking
   * one read would have nothing to write to, and "Mark all as read" must not appear to clear them.
   *
   * Polled on the same interval as the unread count so the bell's badge and its contents cannot
   * disagree about how many things need attention.
   */
  const { data: expiryAlerts = [] } = useQuery({
    queryKey: [ProcurementAlertQueryKeys.list],
    queryFn: ({ signal }) => getProcurementExpiryAlerts(signal),
    refetchInterval: UNREAD_POLL_MS,
  });

  /* An expiry alert is unread by nature: nothing has been acknowledged, so it always counts. */
  const unreadCount = storedUnread + expiryAlerts.length;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [NotificationsQueryKeys.list] });
    queryClient.invalidateQueries({ queryKey: [NotificationsQueryKeys.unreadCount] });
  };

  const { mutate: markRead } = useMutation({
    mutationFn: (id: string) => setNotificationRead(id, true),
    onSettled: invalidate,
  });

  const { mutate: markAllRead, isPending: isMarkingAll } = useMutation({
    mutationFn: () =>
      Promise.all(
        notifications.filter((n) => !n.isRead).map((n) => setNotificationRead(n.id, true)),
      ),
    onSettled: invalidate,
  });

  /*
   * The share may belong to another entity than the session's, so the backend answers
   * with the full URL on the owner's host. Same origin stays in the SPA; another host is a full
   * navigation, where the user signs in (or is already signed in) for that entity.
   */
  const openFileShare = async (shareId: string) => {
    try {
      const url = new URL(await getShareOpenUrl(shareId));
      if (url.origin === window.location.origin) {
        navigate(toNavigateTarget(`${url.pathname}${url.search}`));
      } else {
        window.location.assign(url.toString());
      }
    } catch {
      mantineNotifications.show({
        title: "Could not open the share",
        message: "It may have been removed, or you no longer have access.",
        color: "red",
      });
    }
  };

  const handleItemClick = (notification: NotificationItem) => {
    if (!notification.isRead) {
      markRead(notification.id);
    }
    if (notification.resourceType === "file_share" && notification.resourceId) {
      close();
      void openFileShare(notification.resourceId);
      return;
    }
    if (notification.resourceType === "payment_request" && notification.resourceId) {
      close();
      navigate({ to: "/accounts-payable", search: { requestId: notification.resourceId } });
      return;
    }
    /*
     * SERV-2975: a notice about one pricing line. A ticket can carry several, so the ticket id alone
     * opened line 1; the pricing screen forwards a linked line to its ticket on that line, and shows
     * a line raised before tickets and pricing were linked on its own.
     */
    if (notification.resourceType === "procurement_pricing_request" && notification.resourceId) {
      close();
      navigate({
        to: "/procurement/pricing/$pricingRequestId",
        params: { pricingRequestId: notification.resourceId },
      });
      return;
    }
    if (notification.resourceType === "procurement_request" && notification.resourceId) {
      close();
      const requestTab = CATEGORY_META[notification.category]?.requestTab;
      navigate({
        to: "/procurement/$requestId",
        params: { requestId: notification.resourceId },
        ...(requestTab ? { search: { tab: requestTab } } : {}),
      });
      return;
    }
    const route = CATEGORY_META[notification.category]?.route;
    if (route) {
      close();
      navigate({ to: route });
    }
  };

  return (
    <Popover
      opened={isOpen}
      onDismiss={close}
      width={380}
      position="bottom-end"
      shadow="md"
      withArrow
    >
      <Popover.Target>
        <Indicator
          label={unreadCount > 99 ? "99+" : unreadCount}
          size={16}
          color="red"
          disabled={unreadCount === 0}
          offset={4}
        >
          <ActionIcon
            variant="subtle"
            c="var(--icon-default)"
            aria-label="Notifications"
            onClick={toggle}
          >
            <IconBell size={16} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Flex
          justify="space-between"
          align="center"
          p="var(--spacing-small) var(--spacing-medium)"
          style={{ borderBottom: "1px solid var(--border-default)" }}
        >
          <Text fw="var(--font-weight-bold)" c="var(--text-strong)">
            Notifications
          </Text>
          {/*
           * storedUnread, not unreadCount: expiry alerts have no row to mark, so offering to clear
           * them would be a button that visibly does nothing.
           */}
          {storedUnread > 0 && (
            <UnstyledButton onClick={() => markAllRead()} disabled={isMarkingAll}>
              <Text size="sm" c="var(--text-brand-default)" fw="var(--font-weight-semi-bold)">
                Mark all as read
              </Text>
            </UnstyledButton>
          )}
        </Flex>
        <ScrollArea.Autosize mah={420}>
          {/*
           * Expiry alerts first, and outside the loading branch: they load on their own query, so
           * a slow notifications list must not hide a rental that is already overdue.
           */}
          {expiryAlerts.map((alert) => (
            <UnstyledButton
              key={alert.id}
              onClick={() => {
                close();
                /*
                 * SERV-2666: two kinds of alert now, so two destinations. A unit rented through the
                 * units screen has no request to open - sending it to /procurement/$requestId with
                 * a null id would route to a detail page for nothing.
                 */
                if (alert.requestId) {
                  navigate({
                    to: "/procurement/$requestId",
                    params: { requestId: alert.requestId },
                  });
                  return;
                }
                navigate({ to: "/procurement/storage-units" });
              }}
              style={{ display: "block", width: "100%" }}
            >
              <Flex
                gap="var(--spacing-small)"
                p="var(--spacing-small) var(--spacing-medium)"
                style={{ borderBottom: "1px solid var(--border-default)" }}
              >
                <IconAlertTriangle size={16} color={ALERT_TONE[alert.severity]} />
                <Flex direction="column" gap="2px" style={{ minWidth: 0 }}>
                  <Text size="sm" fw="var(--font-weight-semi-bold)" c={ALERT_TONE[alert.severity]}>
                    {ALERT_HEADING[alert.severity]}
                  </Text>
                  <Text size="sm" c="var(--text-default)">
                    {alert.message}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {/* The server formats this: a request number, or the facility and unit. */}
                    {alert.label}
                  </Text>
                </Flex>
              </Flex>
            </UnstyledButton>
          ))}
          {isLoading ? (
            <Center p="var(--spacing-x-large)">
              <Loader size="sm" />
            </Center>
          ) : !notifications.length && !expiryAlerts.length ? (
            <Center p="var(--spacing-x-large)">
              <Text size="sm" c="dimmed">
                No notifications yet
              </Text>
            </Center>
          ) : (
            notifications.map((notification) => {
              const Icon = CATEGORY_META[notification.category]?.icon ?? IconBell;
              return (
                <UnstyledButton
                  key={notification.id}
                  onClick={() => handleItemClick(notification)}
                  w="100%"
                >
                  <Flex
                    gap="var(--spacing-small)"
                    align="flex-start"
                    p="var(--spacing-small) var(--spacing-medium)"
                    bg={notification.isRead ? "transparent" : "var(--bg-brand-inverse-default)"}
                    style={{ borderBottom: "1px solid var(--border-default)" }}
                  >
                    <Box c="var(--icon-default)" mt={2}>
                      <Icon size={16} />
                    </Box>
                    <Box flex={1}>
                      <Text
                        size="sm"
                        c="var(--text-default)"
                        fw={
                          notification.isRead
                            ? "var(--font-weight-regular)"
                            : "var(--font-weight-semi-bold)"
                        }
                      >
                        {notification.notificationText}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                          addSuffix: true,
                        })}
                      </Text>
                    </Box>
                    {!notification.isRead && (
                      <Box
                        w={8}
                        h={8}
                        mt={6}
                        bg="var(--bg-brand-default)"
                        style={{ borderRadius: "50%", flexShrink: 0 }}
                      />
                    )}
                  </Flex>
                </UnstyledButton>
              );
            })
          )}
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}
